import { useEffect, useState } from "react";
import AdminShell from "../../components/AdminShell";
import ConnectorIcon from "../../components/ConnectorIcon";
import { CustomerPanel, MetricCard, StatusBadge, TableWrapper } from "../../components/customer/CustomerUI";
import api from "../../lib/api";

const toneForStatus = (status) => {
  if (["accepted", "applied", "available", "healthy"].includes(status)) return "success";
  if (["failed", "critical"].some((value) => String(status || "").includes(value))) return "danger";
  if (["warning", "pending", "skipped"].some((value) => String(status || "").includes(value))) return "warning";
  return "neutral";
};

export default function CrmOperations() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/crm-operations").then((r) => setData(r.data)).catch(() => {}); }, []);

  const wq = data?.writeback_queue || {};
  const sj = data?.scan_jobs || {};
  const email = data?.email_delivery || {};
  const quotaRows = data?.recent_quota_health || [];

  const STAT = (label, value, cls) => <MetricCard label={label} value={data ? (value ?? 0) : "—"} valueClassName={`text-3xl tracking-tight ${cls}`} labelClassName="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500" />;

  return (
    <AdminShell title="CRM Operations Center" subtitle="Provider-agnostic connector health, scan jobs and write-back queue">
      <h2 className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Scan Jobs</h2>
      <div className="grid grid-cols-3 gap-3">
        {STAT("Active", sj.active, "text-amber-400")}
        {STAT("Completed", sj.completed, "text-emerald-400")}
        {STAT("Failed", sj.failed, "text-red-400")}
      </div>

      <h2 className="mb-3 mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Write-back Queue</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {STAT("Pending", wq.pending, "text-amber-400")}
        {STAT("Applied", wq.applied, "text-emerald-400")}
        {STAT("Failed", wq.failed, "text-red-400")}
        {STAT("Reverted", wq.reverted, "text-slate-700")}
      </div>

      <h2 className="mb-3 mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Email Delivery</h2>
      <div className="grid grid-cols-3 gap-3">
        {STAT("Accepted", email.accepted, "text-emerald-400")}
        {STAT("Failed", email.failed, "text-red-400")}
        {STAT("Skipped", email.skipped, "text-amber-400")}
      </div>
      <TableWrapper className="mt-3" title="Recent email delivery">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-300 text-left font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">
              <th className="px-3 py-2 font-medium">Time</th>
              <th className="px-3 py-2 font-medium">Message</th>
              <th className="px-3 py-2 font-medium">Provider</th>
              <th className="px-3 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {(data?.recent_email_events || []).length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-500">No delivery events yet.</td></tr>
            ) : data.recent_email_events.map((event, index) => (
              <tr key={`${event.created_at}-${index}`} className="border-b border-slate-300 last:border-0">
                <td className="px-3 py-2 font-mono text-slate-500">{event.created_at ? new Date(event.created_at).toLocaleString() : "—"}</td>
                <td className="px-3 py-2 text-slate-900">{event.subject || "Transactional email"}</td>
                <td className="px-3 py-2 font-mono text-slate-600">{event.provider || "—"}</td>
                <td className="px-3 py-2"><StatusBadge tone={toneForStatus(event.status)}>{event.status}</StatusBadge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrapper>

      <h2 className="mb-3 mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Connector Health</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(data?.connectors || []).map((c) => (
          <CustomerPanel key={c.connector_id} data-testid={`crmops-connector-${c.connector_id}`}>
            <div className="px-ui-4 py-ui-4 sm:px-ui-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <ConnectorIcon name={c.icon} className="h-5 w-5 text-slate-700" />
                <span className="font-semibold text-slate-900">{c.display_name}</span>
              </div>
              <StatusBadge tone={toneForStatus(c.status)}>{c.status}</StatusBadge>
            </div>
            <div className="mt-3 flex items-center justify-between font-mono text-xs text-slate-500">
              <span>{c.auth_type}</span>
              <span className="text-slate-700">{c.connections} connection{c.connections === 1 ? "" : "s"}</span>
            </div>
            </div>
          </CustomerPanel>
        ))}
      </div>

      <TableWrapper className="mt-6" data-testid="crmops-quota-health" title="Provider quota health">
        <table className="w-full min-w-[760px] text-xs">
          <thead>
            <tr className="border-b border-slate-300 text-left font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">
              <th className="px-3 py-2 font-medium">Connection</th>
              <th className="px-3 py-2 font-medium">Provider</th>
              <th className="px-3 py-2 font-medium">Daily remaining</th>
              <th className="px-3 py-2 font-medium">Lowest burst</th>
              <th className="px-3 py-2 font-medium">Requests / 429s</th>
              <th className="px-3 py-2 font-medium">Health</th>
              <th className="px-3 py-2 font-medium">Observed</th>
            </tr>
          </thead>
          <tbody>
            {quotaRows.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">No provider quota telemetry yet.</td></tr>
            ) : quotaRows.map((row, index) => {
              const health = row.quota_health || {};
              return (
                <tr key={`${row.connection_label}-${row.completed_at}-${index}`} className="border-b border-slate-300 last:border-0">
                  <td className="px-3 py-2 text-slate-900">{row.connection_label}</td>
                  <td className="px-3 py-2 font-mono text-slate-600">{row.connector_name}</td>
                  <td className="px-3 py-2 font-mono text-slate-700">
                    {health.daily_remaining == null || health.daily_limit == null
                      ? "Not reported"
                      : `${health.daily_remaining.toLocaleString()} / ${health.daily_limit.toLocaleString()}`}
                  </td>
                  <td className="px-3 py-2 font-mono text-slate-700">
                    {health.burst_remaining == null || health.burst_limit == null
                      ? "Not reported"
                      : `${health.burst_remaining} / ${health.burst_limit}`}
                  </td>
                  <td className="px-3 py-2 font-mono text-slate-700">
                    {health.requests_observed ?? 0} / {health.rate_limited_responses ?? 0}
                  </td>
                  <td className="px-3 py-2"><StatusBadge tone={toneForStatus(health.status)}>{health.status || "unknown"}</StatusBadge></td>
                  <td className="px-3 py-2 font-mono text-slate-500">
                    {row.completed_at ? new Date(row.completed_at).toLocaleString() : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableWrapper>

      <TableWrapper className="mt-6" title="Recent write-back activity">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-300 text-left font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">
              <th className="px-3 py-2 font-medium">Contact</th>
              <th className="px-3 py-2 font-medium">Field</th>
              <th className="px-3 py-2 font-medium">New Value</th>
              <th className="px-3 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {(data?.recent_writes || []).length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-500">No write-back activity yet.</td></tr>
            ) : data.recent_writes.map((r) => (
              <tr key={r.id} className="border-b border-slate-300 last:border-0">
                <td className="px-3 py-2 text-slate-900">{r.contact_name || r.contact_id}</td>
                <td className="px-3 py-2 font-mono text-slate-600">{r.field}</td>
                <td className="px-3 py-2 font-mono text-slate-700">{r.new_value}</td>
                <td className="px-3 py-2"><StatusBadge tone={toneForStatus(r.status)}>{r.status}</StatusBadge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrapper>
    </AdminShell>
  );
}
